import axios from 'axios';

const { REACT_APP_API_URL, NODE_ENV, REACT_APP_ENVIRONMENT } = process.env;
// Use custom environment variable if set, otherwise fall back to NODE_ENV  
const ENVIRONMENT = REACT_APP_ENVIRONMENT || NODE_ENV;

// Create axios instance with base configuration
const axiosInstance = axios.create({
  baseURL: REACT_APP_API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Token management utilities
const TokenManager = {
  getToken: () => {
    return window.localStorage.getItem('JWT_TOKEN');
  },
  
  setToken: (token) => {
    if (token) {
      window.localStorage.setItem('JWT_TOKEN', token);
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      window.localStorage.removeItem('JWT_TOKEN');
      delete axiosInstance.defaults.headers.common['Authorization'];
    }
  },
  
  clearToken: () => {
    window.localStorage.removeItem('JWT_TOKEN');
    delete axiosInstance.defaults.headers.common['Authorization'];
  },
  
  initializeToken: () => {
    const token = TokenManager.getToken();
    if (token) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }
};

// Initialize token on module load
TokenManager.initializeToken();

// Request interceptor to ensure token is always set
axiosInstance.interceptors.request.use(
  (config) => {
    const token = TokenManager.getToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh logic
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isPusherRequest = originalRequest.url.includes('/broadcasting/auth');
    const isRefreshRequest = originalRequest.url.includes('auth/refresh');
    
    // Don't retry Pusher auth requests or refresh requests
    if (error.response?.status === 401 && !originalRequest._retry 
        && !isPusherRequest && !isRefreshRequest) {
      originalRequest._retry = true;
      
      try {
        // Import refreshToken dynamically to avoid circular dependency
        const { refreshToken } = await import('../authUtils');
        const newToken = await refreshToken();
        
        if (newToken) {
          // Update token using TokenManager
          TokenManager.setToken(newToken);
          
          // Update the original request with new token
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          
          // If Pusher is connected, disconnect and reconnect with new token
          if (window.Echo) {
            window.Echo.disconnect();
            window.Echo = null;
            // The PusherManager will automatically reconnect
          }
          
          return axiosInstance(originalRequest);
        }
        
        // Handle failed refresh - redirect based on environment
        TokenManager.clearToken();
        const isProduction = ENVIRONMENT?.toLowerCase() === 'production';
        
        if (!isProduction) {
          console.log(`Failed to refresh token! ${ENVIRONMENT} environment - redirecting to local login...`);
          window.location.href = '/';
        } else {
          console.log('Failed to refresh token! Production environment - redirecting to WIX website...');
          window.location.href = 'https://www.bridgepalkids.com/';
        }
        return Promise.reject(error);
      } catch (refreshError) {
        console.error('Token refresh error:', refreshError);
        TokenManager.clearToken();
        const isProduction = ENVIRONMENT?.toLowerCase() === 'production';
        
        if (!isProduction) {
          console.error(`Failed to refresh token! ${ENVIRONMENT} environment - redirecting to local login...`);
          window.location.href = '/';
        } else {
          console.error('Failed to refresh token! Production environment - redirecting to WIX website...');
          window.location.href = 'https://www.bridgepalkids.com/';
        }
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;
export { TokenManager }; 